import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { Drive, EngineTechnology } from '~/graphql/__generated__/graphql';
import { DriveTrain } from '~/graphql/mapper';
import { state } from '~/storage';

export type DriveTrainFilterGeartype = 'AUTOMATIC' | 'MANUAL';
export type DriveTrainFilterEngineTechnology = 'PETROL' | 'DIESEL' | 'HYBRID' | 'ELECTRO';
export type DriveTrainFilterDrive = 'QUATTRO' | 'FRONT_WHEEL_DRIVE' | 'REAR_WHEEL_DRIVE';

export interface DriveTrainFilters {
	gearType: Array<DriveTrainFilterGeartype>;
	engineTechnology: Array<DriveTrainFilterEngineTechnology>;
	drive: Array<DriveTrainFilterDrive>;
}

export const useDriveTrainFilter = (drivetrains: DriveTrain[]) => {
	const [filters, setFilters] = useState<DriveTrainFilters>({ gearType: [], engineTechnology: [], drive: [] });
	const [, setConsumptionDisclaimers] = useAtom(state.consumptionDisclaimersAtom);
	useEffect(() => {
		const newFilters: DriveTrainFilters = { gearType: [], engineTechnology: [], drive: [] };

		drivetrains.forEach((drivetrain) => {
			const gearType = drivetrain.techData?.modelAttributes?.gearType;
			if (gearType && !newFilters.gearType.includes(gearType)) {
				newFilters.gearType.push(gearType);
			}

			const engineTechnology = mapEngineTechnologyToFilterValue(
				drivetrain?.techData?.modelAttributes?.engineTechnology as EngineTechnology,
			);

			if (!newFilters.engineTechnology.includes(engineTechnology)) {
				newFilters.engineTechnology.push(engineTechnology);
			}

			const drive = mapDriveToFilterValue(drivetrain.techData?.modelAttributes?.drive as Drive);

			if (drive && !newFilters.drive.includes(drive)) {
				newFilters.drive.push(drive);
			}

			if (drivetrain.status.selected === true) {
				const disclaimers = drivetrain.techData?.consumptionAndEmission?.disclaimers.map(
					(disclaimer) => disclaimer.value,
				);
				setConsumptionDisclaimers(disclaimers!);
			}
		});

		setFilters(newFilters);
	}, [JSON.stringify(drivetrains)]);

	const usedFilter = useMemo(() => {
		// filters out all filters with less than 2 values to prevent filter categories with only one value
		for (const [key, value] of Object.entries(filters)) {
			if (value.length < 2) {
				delete filters[key as keyof DriveTrainFilters];
			}
		}

		// returns filters if at least one filter has more than 1 value
		return Object.keys(filters).some((filter) => filter.length > 0) ? filters : undefined;
	}, [JSON.stringify(filters)]);

	return usedFilter;
};

// Needed because there are multiple values which are translated into "PETROL", so we cannot simply use the backend values
export const mapEngineTechnologyToFilterValue = (value?: EngineTechnology | null) => {
	if (!value) return 'ELECTRO';
	return {
		TFSI: 'PETROL',
		FSI: 'PETROL',
		TFSIE: 'HYBRID',
		TDI: 'DIESEL',
	}[value] as DriveTrainFilterEngineTechnology;
};

// Needed because there are multiple values which are translated into "QUATTRO", so we cannot simply use the backend values
export const mapDriveToFilterValue = (value?: Drive | null) => {
	if (!value) return '';
	return {
		QUATTRO: 'QUATTRO',
		'QUATTRO-ULTRA': 'QUATTRO',
		FRONT_WHEEL_DRIVE: 'FRONT_WHEEL_DRIVE',
		REAR_WHEEL_DRIVE: 'REAR_WHEEL_DRIVE',
	}[value] as DriveTrainFilterDrive;
};
